import { UcoastEl } from '@/scripts/core/UcoastEl'
import EmblaCarousel, {
	type EmblaCarouselType,
	EmblaEventType,
	EmblaOptionsType,
	EmblaPluginType,
} from 'embla-carousel'
import Autoplay from 'embla-carousel-autoplay'
import AutoHeight from 'embla-carousel-auto-height'
import ClassNames from 'embla-carousel-class-names'
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures'
import { getAttributeOrUndefined, qsRequired } from '@/scripts/core/global'

// https://www.embla-carousel.com/

EmblaCarousel.globalOptions = { loop: false }

export type EmblaCustomOptionsType = {
	fade?: EmblaBreakpointType
	breakpoint?: EmblaBreakpointType
}

export type EmblaWrapperConstructorType = {
	userOptions: EmblaOptionsType
	enabledPlugins?: {
		autoPlay?: boolean
		autoHeight?: boolean
		classNames?: boolean
		wheelGestures?: boolean
	}
	customOptions?: EmblaCustomOptionsType
}

export type EmblaBreakpointType = 'all' | 's-up' | 's-down'

export function breakpointCallback(
	breakpoint: EmblaBreakpointType,
	callback: () => void
) {
	if (breakpoint === 'all') {
		callback()
		return
	}
	const breakpointQuery =
		breakpoint === 's-up' ? '(min-width: 750px)' : '(max-width: 749px)'
	const breakpointMediaQuery = window.matchMedia(breakpointQuery)
	if (breakpointMediaQuery.matches) {
		callback()
	}
}

export class EmblaBase extends UcoastEl {
	static htmlSelector = 'embla-base'
	emblaNode: HTMLElement
	emblaApi: EmblaCarouselType
	userOptions: EmblaOptionsType
	plugins: EmblaPluginType[]
	customOptions?: EmblaCustomOptionsType
	constructor() {
		super()
		const { userOptions, enabledPlugins, customOptions } = this.getOptions()
		this.emblaNode = qsRequired('.embla', this)

		const plugins: EmblaPluginType[] = []
		if (enabledPlugins !== undefined) {
			if (enabledPlugins.autoPlay) {
				plugins.push(Autoplay())
			}
			if (enabledPlugins.autoHeight) {
				plugins.push(AutoHeight())
			}
			if (enabledPlugins.classNames) {
				plugins.push(ClassNames({ inView: 'is-selected' }))
			}
			if (enabledPlugins.wheelGestures) {
				plugins.push(WheelGesturesPlugin())
			}
		}
		this.userOptions = userOptions
		this.plugins = plugins
		this.customOptions = customOptions
		this.emblaApi = this.initEmbla()
		this.emblaListeners()
		this.applyCustomOptions()
	}
	initEmbla() {
		return EmblaCarousel(this.emblaNode, this.userOptions, this.plugins)
	}
	emblaListeners() {
		defaultEmblaListeners(this.emblaApi)
	}
	applyCustomOptions() {
		if (!this.customOptions) return
		if (this.customOptions.fade) {
			breakpointCallback(
				this.customOptions.fade,
				this.fadeOverrides.bind(this)
			)
			// https://github.com/davidjerleke/embla-carousel/issues/39
		}
	}
	fadeOverrides() {
		fadeOverrides(this.emblaApi)
	}
	getCustomOptions(): EmblaCustomOptionsType {
		const fade = getAttributeOrUndefined(
			'data-embla-fade',
			this
		) as EmblaBreakpointType
		const breakpoint = getAttributeOrUndefined(
			'data-embla-breakpoint',
			this
		) as EmblaBreakpointType
		return {
			fade,
			breakpoint: breakpoint ?? 'all',
		}
	}
	getOptions() {
		const customOptions = this.getCustomOptions()
		return {
			userOptions: { loop: false },
			enabledPlugins: undefined,
			customOptions,
		} as EmblaWrapperConstructorType
	}
}

export function toggleEmblaReady(
	emblaApi: EmblaCarouselType,
	event: EmblaEventType
) {
	const isResizeEvent = event === 'resize'
	const rootNode = emblaApi.rootNode()
	if (!(rootNode instanceof HTMLElement)) return
	if (isResizeEvent) {
		rootNode.classList.remove('embla--is-ready')
	} else {
		rootNode.classList.add('embla--is-ready')
	}

	if (isResizeEvent) {
		emblaApi.reInit()
	}
}

export function fadeOverrides(emblaApi: EmblaCarouselType) {
	emblaApi.internalEngine().translate.toggleActive(false)
	emblaApi.internalEngine().translate.clear()
}

export function defaultEmblaListeners(emblaApi: EmblaCarouselType) {
	emblaApi
		.on('init', (_, event) => {
			console.log('initialized', event)
		})
		.on('init', toggleEmblaReady)
		.on('resize', toggleEmblaReady)
		.on('reInit', toggleEmblaReady)
}
